import { collection, deleteDoc, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { FIREBASE_COLLECTIONS } from "config/firebaseCollections";
import {
  formatDateKey,
  normalizeDateInput as normalizeScheduleDateInput,
  resolveScheduleForDate,
} from "utils/scheduleResolver";

export const CLASS_NOTE_FIELDS = [
  "plan",
  "reflection",
  "homework",
  "objectives",
  "materials",
  "behaviorNotes",
  "announcement",
];

export const CLASS_NOTE_RICH_TEXT_FIELDS = [
  "plan",
  "reflection",
  "homework",
  "materials",
  "behaviorNotes",
];

export const CLASS_NOTE_PRINT_SECTIONS = [
  { key: "classNoteAnnouncement", field: "announcement", defaultLabel: "Announcements", defaultActive: true },
  { key: "objectives", field: "objectives", defaultLabel: "Objectives", defaultActive: true },
  { key: "plan", field: "plan", defaultLabel: "Lesson Plan", defaultActive: true },
  { key: "materials", field: "materials", defaultLabel: "Materials", defaultActive: true },
  { key: "homework", field: "homework", defaultLabel: "Homework", defaultActive: true },
  { key: "behaviorNotes", field: "behaviorNotes", defaultLabel: "Behavior Notes", defaultActive: false },
  { key: "reflection", field: "reflection", defaultLabel: "Reflection", defaultActive: false },
];

export const CLASS_NOTE_SECTION_ICONS = {
  plan: "fa fa-list-alt",
  contentPreview: "fa fa-eye",
  materials: "fa fa-folder-open-o",
  announcement: "fa fa-bullhorn",
  objectives: "fa fa-flag",
  homework: "fa fa-pencil",
  reflection: "fa fa-lightbulb-o",
  behaviorNotes: "fa fa-users",
};

export const formatClassNoteDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  return value.toLocaleDateString("en-CA");
};

export const createEmptyClassNote = (noteDate = "") => ({
  noteDate: formatClassNoteDate(noteDate),
  plan: "",
  reflection: "",
  homework: "",
  objectives: "",
  materials: "",
  behaviorNotes: "",
  announcement: "",
});

export const isClassNoteWeekendDate = (date) => {
  const normalizedDate = normalizeScheduleDateInput(date);
  if (!normalizedDate || Number.isNaN(normalizedDate.getTime())) return false;
  const day = normalizedDate.getDay();
  return day === 0 || day === 6;
};

export const isClassNoteExplicitNoSchoolDate = ({ userSettings = {}, date } = {}) =>
  resolveScheduleForDate(userSettings, date)?.isNoSchool === true;

export const isClassNoteNonSchoolDate = ({ userSettings = {}, date } = {}) =>
  isClassNoteWeekendDate(date) || isClassNoteExplicitNoSchoolDate({ userSettings, date });

export const getNextClassNoteSchoolDate = ({ userSettings = {}, date, maxDays = 370 } = {}) => {
  const normalizedDate = normalizeScheduleDateInput(date);
  if (!normalizedDate || Number.isNaN(normalizedDate.getTime())) return null;

  for (let offset = 1; offset <= maxDays; offset += 1) {
    const candidateDate = new Date(normalizedDate);
    candidateDate.setDate(normalizedDate.getDate() + offset);
    if (!isClassNoteNonSchoolDate({ userSettings, date: candidateDate })) {
      return {
        date: candidateDate,
        dateKey: formatDateKey(candidateDate),
      };
    }
  }

  return null;
};

const CLASS_NOTE_PLAN_LARGE_TEXT_CLASS = "class-note-plan-text-large";

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const decodeHtmlEntities = (value = "") => {
  const rawValue = String(value || "");
  if (!rawValue) return "";

  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = rawValue;
    return textarea.value;
  }

  return rawValue
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
};

const hasHtmlTags = (value = "") => /<\/?[a-z][\s\S]*>/i.test(value);

const getFallbackPlainTextFromHtml = (value = "") =>
  String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:p|li|div|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const normalizePlanTextContent = (value = "") => String(value || "").replace(/\u00a0/g, " ").trim();

const stripListMarker = (value = "") =>
  String(value || "")
    .replace(/^[-*•‣◦▪▫]\s+/, "")
    .replace(/^\d+[\.)]\s+/, "")
    .replace(/^[A-Za-z][\.)]\s+/, "")
    .trim();

const plainTextToClassNotePlanHtml = (value = "") => {
  const lines = String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return "";
  return lines.map((line) => `<p>${escapeHtml(line)}</p>`).join("");
};

const sanitizeClassNoteInlineNode = (node) => {
  if (!node) return "";

  if (node.nodeType === 3) {
    return escapeHtml(node.textContent || "");
  }

  if (node.nodeType !== 1) {
    return "";
  }

  const tagName = node.nodeName.toLowerCase();
  const childHtml = () => Array.from(node.childNodes).map(sanitizeClassNoteInlineNode).join("");

  if (tagName === "script" || tagName === "style") return "";
  if (tagName === "br") return " ";
  if (tagName === "strong" || tagName === "b") return `<strong>${childHtml()}</strong>`;
  if (tagName === "em" || tagName === "i") return `<em>${childHtml()}</em>`;
  if (tagName === "u") return `<u>${childHtml()}</u>`;

  return childHtml();
};

export const extractClassNoteInlinePlainText = (value = "") => {
  const rawValue = typeof value === "string" ? value : "";
  if (!rawValue) return "";
  if (!hasHtmlTags(rawValue)) return normalizePlanTextContent(decodeHtmlEntities(rawValue));

  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return normalizePlanTextContent(getFallbackPlainTextFromHtml(rawValue));
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${rawValue}</div>`, "text/html");
  return normalizePlanTextContent(doc.body.firstChild?.textContent || "");
};

export const sanitizeClassNoteInlineHtml = (value = "") => {
  const rawValue = typeof value === "string" ? value.trim() : "";
  if (!rawValue) return "";

  if (!hasHtmlTags(rawValue)) return escapeHtml(decodeHtmlEntities(rawValue));

  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return escapeHtml(getFallbackPlainTextFromHtml(rawValue));
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${rawValue}</div>`, "text/html");
  return Array.from(doc.body.firstChild?.childNodes || [])
    .map(sanitizeClassNoteInlineNode)
    .join("")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
};

export const normalizeClassNoteRichLineRow = (row = "") => {
  const fallbackText = typeof row === "string" ? row : row?.text || "";
  const sourceHtml = typeof row === "string" ? row : row?.html || fallbackText;
  const html = sanitizeClassNoteInlineHtml(sourceHtml);
  const text = extractClassNoteInlinePlainText(html || fallbackText);

  return {
    text,
    html: html || escapeHtml(text),
  };
};

const parseRichLineRowsFromHtml = (value = "") => {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return parseClassNoteLineItems(getFallbackPlainTextFromHtml(value)).map(normalizeClassNoteRichLineRow);
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${value}</div>`, "text/html");
  const output = [];
  const seen = new Set();

  const addRow = (html) => {
    const row = normalizeClassNoteRichLineRow({ html });
    if (!row.text) return;
    const key = row.text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    output.push(row);
  };

  const visitNode = (node) => {
    if (node.nodeType === 1 && ["ul", "ol"].includes(node.nodeName.toLowerCase())) {
      Array.from(node.children || []).forEach((child) => {
        if (child.nodeName.toLowerCase() === "li") addRow(child.innerHTML);
      });
      return;
    }

    if (node.nodeType === 1 && node.querySelector?.("ul, ol")) {
      Array.from(node.childNodes || []).forEach(visitNode);
      return;
    }

    if (node.nodeType === 1 && ["p", "div", "li"].includes(node.nodeName.toLowerCase())) {
      addRow(node.innerHTML);
      return;
    }

    addRow(node.nodeType === 3 ? node.textContent : node.outerHTML);
  };

  Array.from(doc.body.firstChild?.childNodes || []).forEach(visitNode);

  return output;
};

const sanitizeClassNotePlanNode = (node, depth = 0) => {
  if (!node) return "";

  if (node.nodeType === 3) {
    return escapeHtml(node.textContent || "");
  }

  if (node.nodeType !== 1) {
    return "";
  }

  const tagName = node.nodeName.toLowerCase();
  const childHtml = () => Array.from(node.childNodes).map((child) => sanitizeClassNotePlanNode(child, depth)).join("");
  const nestedListHtml = () =>
    Array.from(node.childNodes)
      .map((child) => {
        if (child.nodeType === 1 && child.nodeName.toLowerCase() === "li") {
          return sanitizeClassNotePlanNode(child, depth + 1);
        }

        const sanitizedChild = sanitizeClassNotePlanNode(child, depth + 1);
        const textContent = child.textContent || "";
        if (!normalizePlanTextContent(textContent)) return "";

        return `<li>${sanitizedChild}</li>`;
      })
      .join("");

  if (tagName === "script" || tagName === "style") return "";
  if (tagName === "br") return "<br>";
  if (tagName === "p") return `<p>${childHtml()}</p>`;
  if (tagName === "strong" || tagName === "b") return `<strong>${childHtml()}</strong>`;
  if (tagName === "em" || tagName === "i") return `<em>${childHtml()}</em>`;
  if (tagName === "u") return `<u>${childHtml()}</u>`;
  if (tagName === "mark") return `<mark>${childHtml()}</mark>`;
  if (tagName === "ul" && depth < 3) return `<ul>${nestedListHtml()}</ul>`;
  if (tagName === "ol" && depth < 3) return `<ol>${nestedListHtml()}</ol>`;
  if (tagName === "li") return `<li>${childHtml()}</li>`;
  if (tagName === "span" && node.classList?.contains(CLASS_NOTE_PLAN_LARGE_TEXT_CLASS)) {
    return `<span class="${CLASS_NOTE_PLAN_LARGE_TEXT_CLASS}">${childHtml()}</span>`;
  }

  return childHtml();
};

const sanitizeClassNotePlanHtml = (value = "") => {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return plainTextToClassNotePlanHtml(getFallbackPlainTextFromHtml(value));
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${value}</div>`, "text/html");
  return Array.from(doc.body.firstChild?.childNodes || [])
    .map((child) => sanitizeClassNotePlanNode(child))
    .join("");
};

export const normalizeClassNotePlanHtml = (value = "") => {
  const rawValue = typeof value === "string" ? value.trim() : "";
  if (!rawValue) return "";

  const normalizedHtml = hasHtmlTags(rawValue)
    ? sanitizeClassNotePlanHtml(rawValue)
    : plainTextToClassNotePlanHtml(rawValue);

  if (!normalizePlanTextContent(extractClassNotePlanPlainText(normalizedHtml))) {
    return "";
  }

  return normalizedHtml;
};

export const extractClassNotePlanPlainText = (value = "") => {
  const rawValue = typeof value === "string" ? value : "";
  if (!rawValue) return "";
  if (!hasHtmlTags(rawValue)) return rawValue.trim();

  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return getFallbackPlainTextFromHtml(rawValue);
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${rawValue}</div>`, "text/html");
  const output = [];

  const visitNode = (node) => {
    if (node.nodeType === 3) {
      const text = normalizePlanTextContent(node.textContent || "");
      if (text) output.push(text);
      return;
    }

    if (node.nodeType !== 1) return;

    const tagName = node.nodeName.toLowerCase();
    if (tagName === "br") {
      output.push("\n");
      return;
    }

    Array.from(node.childNodes).forEach(visitNode);
    if (["p", "li", "div"].includes(tagName)) {
      output.push("\n");
    }
  };

  Array.from(doc.body.firstChild?.childNodes || []).forEach(visitNode);

  return output
    .join(" ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const normalizeClassNote = (record = {}, noteDate = "") => {
  const normalizedDate = formatClassNoteDate(record?.noteDate || noteDate);
  const base = createEmptyClassNote(normalizedDate);
  CLASS_NOTE_FIELDS.forEach((field) => {
    base[field] = typeof record?.[field] === "string" ? record[field] : "";
  });
  CLASS_NOTE_RICH_TEXT_FIELDS.forEach((field) => {
    base[field] = normalizeClassNotePlanHtml(base[field]);
  });
  return base;
};

export const parseClassNoteLineItems = (value) => {
  if (typeof value !== "string") return [];
  if (hasHtmlTags(value)) {
    return parseRichLineRowsFromHtml(value).map((row) => row.text);
  }

  const output = [];
  const seen = new Set();
  value
    .split("\n")
    .map((line) => stripListMarker(decodeHtmlEntities(line.trim())))
    .forEach((line) => {
      if (!line) return;
      const key = line.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      output.push(line);
    });
  return output;
};

export const parseClassNoteRichLineItems = (value) => {
  if (typeof value !== "string") return [];

  if (!hasHtmlTags(value)) {
    return parseClassNoteLineItems(value).map(normalizeClassNoteRichLineRow);
  }

  return parseRichLineRowsFromHtml(value);
};

export const serializeClassNoteRichLineItems = (rows = []) => {
  const normalizedRows = rows.map(normalizeClassNoteRichLineRow).filter((row) => row.text);
  if (!normalizedRows.length) return "";

  const hasRichFormatting = normalizedRows.some((row) => row.html !== escapeHtml(row.text));
  if (!hasRichFormatting) {
    return normalizedRows.map((row) => row.text).join("\n");
  }

  return normalizedRows.map((row) => `<p>${row.html}</p>`).join("");
};

export const getClassNoteDocRef = (db, userId, classId, noteDate) =>
  doc(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES,
    classId,
    "classNotes",
    formatClassNoteDate(noteDate)
  );

export const getClassNotesCollectionRef = (db, userId, classId) =>
  collection(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId, "classNotes");

export const getClassNoteByDate = async ({ db, userId, classId, noteDate }) => {
  const resolvedDate = formatClassNoteDate(noteDate);
  if (!db || !userId || !classId || !resolvedDate) {
    return {
      exists: false,
      note: createEmptyClassNote(resolvedDate),
    };
  }

  const noteDocRef = getClassNoteDocRef(db, userId, classId, resolvedDate);
  const noteSnap = await getDoc(noteDocRef);

  if (!noteSnap.exists()) {
    return {
      exists: false,
      note: createEmptyClassNote(resolvedDate),
    };
  }

  return {
    exists: true,
    note: normalizeClassNote(noteSnap.data(), resolvedDate),
  };
};

export const upsertClassNoteByDate = async ({ db, userId, classId, noteDate, payload = {}, updatedBy }) => {
  const resolvedDate = formatClassNoteDate(noteDate);
  if (!db || !userId || !classId || !resolvedDate) {
    throw new Error("Missing required class note inputs.");
  }

  const noteDocRef = getClassNoteDocRef(db, userId, classId, resolvedDate);
  const existingSnap = await getDoc(noteDocRef);
  const normalizedPayload = normalizeClassNote(payload, resolvedDate);

  await setDoc(
    noteDocRef,
    {
      ...normalizedPayload,
      noteDate: resolvedDate,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || userId,
      createdAt: existingSnap.exists() ? existingSnap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
    },
    { merge: true }
  );

  return normalizedPayload;
};

export const clearClassNoteByDate = async ({ db, userId, classId, noteDate }) => {
  const resolvedDate = formatClassNoteDate(noteDate);
  if (!db || !userId || !classId || !resolvedDate) {
    throw new Error("Missing required class note inputs.");
  }
  const noteDocRef = getClassNoteDocRef(db, userId, classId, resolvedDate);
  await deleteDoc(noteDocRef);
};
